import { Column, EmptyDiv, FormContext } from '@/components/SettingsLayout';
import SkeletonList from '@/components/SkeletonLoader/SkeletonList';
import { useGetUser } from '@/hooks/useGetUser';
import {
  useGetDefaultPricingConfigurationQuery,
  useGetMinimumPricingConfigurationQuery,
  useUpsertMinimumPricingConfigurationMutation,
} from '@/store/slices/pricingConfigurationSlice';
import { Text } from '@/ui';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PricingConfigurationForm from '@/pages/pricing-forms/PricingConfigurationForm';
import PricingDiffFooter from '@/pages/pricing-forms/PricingDiffFooter';
import { usePricingForm } from '@/pages/pricing-forms/usePricingForm';
import { usePricingChannel } from '@/pages/pricing-forms/usePricingChannel';
import { channelValues, toRates, versionOf } from '@/services/Pricing/pricing-configuration.service';

function FinalPricing() {
  const { t } = useTranslation();
  const { currentUser } = useGetUser();
  const organizationId = currentUser.companyId;
  const queryArg = organizationId ? { organizationId } : skipToken;

  const {
    data: defaults,
    isLoading: isDefaultsLoading,
    isError: isDefaultsError,
  } = useGetDefaultPricingConfigurationQuery(queryArg);
  const { data: minimums, isLoading: isMinimumsLoading } =
    useGetMinimumPricingConfigurationQuery(queryArg);

  const [upsertMinimumPricingConfig, { isLoading: isSaving }] =
    useUpsertMinimumPricingConfigurationMutation();

  const { channel, setChannel } = usePricingChannel();
  const channelDefaults = useMemo(() => channelValues(defaults, channel), [defaults, channel]);
  const channelMinimums = useMemo(() => channelValues(minimums, channel), [minimums, channel]);
  const existingRates = useMemo(() => toRates(minimums), [minimums]);

  const { activeCountry, setActiveCountry, submitHandler, savedAt } = usePricingForm({
    baseline: channelDefaults,
    existing: channelMinimums,
    existingRates,
    channel,
    submit: (nextRates) =>
      upsertMinimumPricingConfig({
        organizationId,
        payload: { rates: nextRates, expectedUpdatedAt: versionOf(minimums) },
      }),
    messages: {
      success: t('Messages.minimum_pricing_update_success'),
      error: t('Messages.minimum_pricing_update_error'),
      conflict: t('Messages.pricing_update_conflict'),
    },
  });

  if (isDefaultsLoading || isMinimumsLoading) {
    return <SkeletonList />;
  }

  if (isDefaultsError || !defaults) {
    return (
      <EmptyDiv>
        <Text size='s' weight='medium'>
          {t('Errors.error_loading_pricing')}
        </Text>
      </EmptyDiv>
    );
  }

  return (
    <FormContext submitHandler={submitHandler} mode='onChange' defaultValues={{ channel, rows: [] }}>
      <Column gapSize='24'>
        <Column>
          <Text size='m' weight='semi-bold'>
            {t('Accounts.processing_fees')}
          </Text>
          <Text size='s' weight='light' color='base-grey'>
            {t('Accounts.processing_fees_comment')}
          </Text>
          <PricingConfigurationForm
            mode='minimum'
            defaults={channelDefaults}
            existingValues={channelMinimums}
            isReadOnly={false}
            activeCountry={activeCountry}
            onActiveCountryChange={setActiveCountry}
            channel={channel}
            onChannelChange={setChannel}
            savedAt={savedAt}
          />
        </Column>
        <PricingDiffFooter
          baseline={channelDefaults}
          existing={channelMinimums}
          isSaving={isSaving}
          isReadOnly={false}
          activeCountry={activeCountry}
        />
      </Column>
    </FormContext>
  );
}

export default FinalPricing;
